import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { OffboardingFields } from './OffboardingFields';
import { todayLocalISO } from '../../utils/formatters';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface DeactivatePersonDialogProps {
  open: boolean;
  personName: string;
  onClose: () => void;
  /** Resolves when the deactivation succeeded; rejections are shown inline. */
  onConfirm: (offboardingDate: string, offboardingReason: string) => Promise<void>;
}

export const DeactivatePersonDialog: React.FC<DeactivatePersonDialogProps> = ({
  open,
  personName,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');
  const [offboardingDate, setOffboardingDate] = useState(todayLocalISO());
  const [offboardingReason, setOffboardingReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setOffboardingDate(todayLocalISO());
      setOffboardingReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const isFutureDate = !!offboardingDate && offboardingDate > todayLocalISO();

  const handleConfirm = async () => {
    // A future date already disables confirm and shows the inline message.
    if (!offboardingDate) {
      setError(t('offboarding.validation.required'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(offboardingDate, offboardingReason.trim());
    } catch (err: any) {
      setError(err.response?.data?.message || t('offboarding.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md" data-testid="deactivate-person-dialog">
        <DialogHeader>
          <DialogTitle>{t('offboarding.title', { name: personName })}</DialogTitle>
          <DialogDescription>{t('offboarding.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <OffboardingFields
            offboardingDate={offboardingDate}
            offboardingReason={offboardingReason}
            onDateChange={(value) => {
              setOffboardingDate(value);
              setError(null);
            }}
            onReasonChange={setOffboardingReason}
            idPrefix="offboarding-dialog"
            testIdPrefix="deactivate-person"
            showDateHelper
            reasonAsTextarea
          />

          {(error || isFutureDate) && (
            <Alert variant="destructive" data-testid="deactivate-person-error">
              <AlertDescription>
                {error || t('offboarding.validation.futureDate')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="deactivate-person-button-cancel"
          >
            {tCommon('buttons.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={submitting || isFutureDate}
            data-testid="deactivate-person-button-confirm"
          >
            {submitting ? tCommon('saving') : t('offboarding.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
